import { useCallback, useState } from 'react';
import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid-premium';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import EditSaleDateCell from '../components/cells/EditSaleDateCell';
import EditEventUrlCell from '../components/cells/EditEventUrlCell';
import { REWARD_OPTIONS } from '@/constants/enums';
import { selectAllBuyManagers } from '@/store/slices/buyManager/buyManagers';
import { useSelector } from 'react-redux';
import { selectAllStrategyTypes } from '@/store/slices/strategyType/strategyTypes';
import { Checkbox, Chip, MenuItem, Select } from '@mui/material';
import { STN_FE_URL } from '@/constants';
import VenueDataCell from '../components/cells/VenueDataCell';
import EventDateCell from '../components/cells/EventDateCell';
import EditLimitCell from '../components/cells/EditLimitCell';
import EditTextareaCell from '../components/cells/EditTextareaCell';
import { useHideEventsMutation } from '@/services/swarmsApi';
import SalesDateCell from '../components/cells/SalesDateCell';
import { CircularProgress } from "@mui/material";
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';


export const useSwarmColumns = ({
  startSwarm,
  stopSwarm,
  rowModesModel,
  swarmsUpdating,
  processRowUpdate,
  onOpenAutobuyModal
}) => {

  const buyManagers = useSelector(selectAllBuyManagers);
  const strategyTypes = useSelector(selectAllStrategyTypes);

  const [hideEvents] = useHideEventsMutation();


  function getEventUrl(row) {
    return row.alternativeEventUrl || row.entityLink?.href;
  }
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  const onHideEventClicked = async (e, info) => {
    try {
      await hideEvents([info]).unwrap();
    } catch (error) {
      if (error.status == 401) return;
      throw error;
    }
  }

  const getColumns = useCallback(() =>{

    const savedColumnWidths = JSON.parse(sessionStorage.getItem('columnWidths')) || {};

    return([
      {
        field: "hideEvent",
        type: 'actions',
        width: savedColumnWidths.hideEvent || 25,
        minWidth: 20,
        aggregable: false,
        getActions: (params) => {
          if (params.row.bteId && !params.row.hideLoaded)
            return [
              <GridActionsCellItem
                icon={<VisibilityOffOutlinedIcon sx={{ fontSize: '1.25em', color: 'var(--orange)' }} />}
                onClick={x => { params.row.hideLoaded = true; onHideEventClicked(x, params.row.bteId)}}
                label="Hide event"
              />
            ]
          else if (params.row.hideLoaded) 
            return [
              <GridActionsCellItem
                icon={<CircularProgress size="1.25em"/>}
              />
            ]
          else 
            return [];
        }
      },
      {
        field: "flag",
        headerName: "",
        editable: false,
        disableColumnMenu: true,
        filterable: false,
        hideSortIcons: true,
        width: savedColumnWidths.flag || 50,
        renderCell: (params) => {
          return <Checkbox checked={params.row.flag} onChange={event => {
            let oldValue = { ...params.row };
            let newvalue = { ...params.row, flag: event.target.checked };
            processRowUpdate(newvalue, oldValue);
          }} />
        }
      },
      {
        headerName: "", 
        field: "run",
        width: savedColumnWidths.run || 75, 
        renderCell: (params) => {
          return ( 
            params.row.useRunLevelLimit === true && ( 
            <Chip 
              id={params.id}
              label="RUN" 
              style={{backgroundColor: "orange", color: "white", fontWeight: "bolder", borderRadius: "0.5em"}}
              onClick={(event) => getRunID(event, params.row.runId)} 
            />
            )
          )
        },
      },
      {
        headerName: "STN",
        field: "STNlink",
        width: savedColumnWidths.STNlink || 65,
        renderCell: (params) => {
          let baseURL = STN_FE_URL
          baseURL += '#/events-details';
          return (<a href={`${baseURL}/${params.row.bteId}`} target="_blank">{`${params.row.bteId}`}</a>)
        },
      },
      {
        headerName: "EVENT",
        field: "name",
        ...(savedColumnWidths.name ? { width: savedColumnWidths.name } : { flex: 1 }),
        renderCell: (params) => (
          <div>
           {getEventUrl(params.row)
            ? 
              <a href={getEventUrl(params.row)} target="_blank">{params.value}</a>
            : 
              params.value
            }
          </div>
        ),
        renderEditCell: (params) => (
          <EditEventUrlCell params={params} />
        ),
        editable: true,
        type: "string",
      },
      {
        headerName: "",
        field: "EditedEntityLink",
        editable: true,
        flex: 0,
        width:0,
        minWidth: 0,
        maxWidth: 0,
        hidden: true,
        hideSortIcons: true,
        sortable: false,
        hideable: false,
      },
      {
        headerName: "",
        field: "NewEntityLink",
        editable: true,
        flex: 0,
        width:0,
        minWidth: 0,
        maxWidth: 0,
        hidden: true,
        hideSortIcons: true,
        sortable: false,
        hideable: false,
      },
      {
        headerName: "",
        field: "entityLink",
        editable: true,
        flex: 0,
        width:0,
        minWidth: 0,
        maxWidth: 0,
        hidden: true,
        hideSortIcons: true,
        sortable: false,
        hideable: false,
      },
      {
        headerName: "",
        field: "alternativeEventUrl",
        editable: true,
        flex: 0,
        width:0,
        minWidth: 0,
        maxWidth: 0,
        hidden: true,
        hideSortIcons: true,
        sortable: false,
        hideable: false,
      },
      {
        headerName: "VENUE",
        field: "venue",
        ...(savedColumnWidths.venue ? { width: savedColumnWidths.venue } : { flex: 1 }),
        // valueGetter: (v) => v.value?.name + ", " + v.value?.city,
        renderCell: (params) => {
          const venue = params.row.venue;
          const bteId = params.row.bteId;
          return <VenueDataCell bteId={bteId} venue={venue} />;
        },
      },
      {
        headerName: "EVENT DATE",
        field: "date",
        width: savedColumnWidths.date || 160,
        //valueGetter: (v) => EventDateCell(v.value),
        renderCell: (v)=> EventDateCell(v.value),
        sortComparator: (v1, v2) => {
          return new Date(v1) - new Date(v2);
        },
      },
      {
        headerName: "LIMIT",
        field: "eventLimitNumberOfTickets",
        width: savedColumnWidths.eventLimitNumberOfTickets || 52,
        align: 'center',
        editable: true,
        renderEditCell: (params) => (
          <EditLimitCell params={params} />
        ),
      },
      {
        headerName: "SALE DATE",
        field: "salesDate",
        editable: true,
        type: 'singleSelect',
        width: savedColumnWidths.salesDate || 160,
        // valueOptions: getSalesDateOptions,
        renderCell: SalesDateCell,
        renderEditCell: (params) => (
          <EditSaleDateCell params={params}/>
        )
      },
      {
        headerName: "",
        field: "newSalesDates",
        editable: true,
        flex: 0,
        width:0,
        minWidth: 0,
        maxWidth: 0,
        hidden: true,
        hideSortIcons: true,
        sortable: false,
        hideable: false
      },
      {
        headerName: "CODE",
        field: "code",
        width: savedColumnWidths.code || 120,
        editable: true,
        type: 'string',
        renderEditCell: (params) => <EditTextareaCell {...params} />
      },
      {
        headerName: "INSTRUCTIONS",
        field: "instructions",
        ...(savedColumnWidths.instructions ? { width: savedColumnWidths.instructions } : { flex: 1 }),
        editable: true,
        type: 'string',
        renderEditCell: (params) => <EditTextareaCell {...params} />
      },
      {
        headerName: "REWARD",
        field: "reward",
        width: savedColumnWidths.reward || 75,
        align: 'center',
        type: 'singleSelect',
        valueFormatter: ({ value }) => currencyFormatter.format(value),
        valueOptions: REWARD_OPTIONS,
        editable: true,
      },
      {
        headerName: "REWARD TYPE",
        field: "rewardTypeId",
        width: savedColumnWidths.rewardTypeId || 110,
        align: 'center',
        type: 'singleSelect',
        valueOptions: [{ value: 1, label: "/Tix" }, { value: 2, label: "Total" }],
        editable: true,
      },
      {
        headerName: "BUY MANAGER",
        field: 'buyManagerId',
        width: savedColumnWidths.buyManagerId || 150,
        align: 'left',
        type: 'singleSelect',
        valueFormatter: (params) => {
          if (!params.value) return "Buy Manager";
          const BM = buyManagers?.find(x => x.buyManagerID == params.value);
          return BM?.firstName + " " + BM?.lastName;
      },
        valueOptions: buyManagers?.map(BM => ({
          value: BM.buyManagerID,
          label: BM.firstName + " " + BM.lastName
        })),
        editable: true,
        renderEditCell: (params) => {
          const handleChange = (event) => {
            const selectedManagerId = event.target.value;
            const selectedManager = buyManagers.find(bm => bm.buyManagerID === parseInt(selectedManagerId));
            
            params.api.setEditCellValue({ id: params.id, field: 'buyManagerId', value: selectedManagerId });
            
            if (selectedManager?.preferredStrategy) {
              console.log("PARAMETROS", selectedManager.preferredStrategy)
              params.api.setEditCellValue({ id: params.id, field: 'strategyTypeId', value: selectedManager.preferredStrategy });
            }
          };
      
          return (
            <Select
              value={params.value || ""}
              onChange={handleChange}
              fullWidth
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return "";
                const BM = buyManagers.find(bm => bm.buyManagerID === parseInt(selected));
                return BM?.firstName + " " + BM?.lastName;
              }}
            >
              {buyManagers?.map(BM => (
                <MenuItem key={BM.buyManagerID} value={BM.buyManagerID}>
                  {BM.firstName} {BM.lastName}
                </MenuItem>
              ))}
            </Select>
          );
        }
      },
      {
        headerName: 'STRATEGY',
        field: 'strategyTypeId',
        width: savedColumnWidths.strategyTypeId || 150,
        type: 'singleSelect',
        valueFormatter:(params)=>{
          if(!params.value) return "Strategy Type"
          const ST = strategyTypes?.find(x => x.id == params.value)
          return ST?.name
        },
        valueOptions: strategyTypes?.map(ST => {
          return({
            value: ST.id,
            label: ST.name
          })
        }),
        editable: true,
      },
      {
        field: 'actions',
        width: savedColumnWidths.actions || 100,
        type: 'actions',
        getActions: (params) => {
          if(params.row.bteId == 3258628){
            console.log("params.row: ", params.row)
          }
          const isInEditMode = params.row.isInEditMode === GridRowModes.Edit || swarmsUpdating.includes(params.row.bteId) || rowModesModel[params.row.bteId]?.mode === GridRowModes.Edit;
          return [
            // AutoBuy Status
            <GridActionsCellItem
              icon={<SmartToyOutlinedIcon sx={{ color: params.row.isAutoBuyActive ? "green" : "lightgray" }} />}
              label="AutoBuy Settings"
              onClick={() => onOpenAutobuyModal(params.row)}
              showInMenu={false}
            />,
            // Start/Stop
            ...(params.row.running ? [
              <GridActionsCellItem
                icon={<StopCircleIcon color={isInEditMode ? 'disabled' : 'error'} sx={{ fontSize: '1.75em' }} />}
                label="Stop Swarm"
                onClick={() => stopSwarm(params.row)}
                disabled={isInEditMode}
              />
            ] : [
              <GridActionsCellItem
                icon={<PlayCircleFilledIcon color={isInEditMode ? 'disabled' : 'success'} sx={{ fontSize: '1.75em' }} />}
                label="Start Swarm"
                onClick={() => startSwarm(params.row)}
                disabled={isInEditMode}
              />
            ])
          ];

          // return (
          //   <>
          //    <SmartToyOutlinedIcon sx={{ padding:"0", color: true ? "green" : "lightgray"}} />
          //     {params.row.running ? [
          //       <GridActionsCellItem
          //         icon={<StopCircleIcon color={isInEditMode ? 'disabled' : 'error'} sx={{ fontSize: '1.75em' }} />}
          //         label="Stop Swarm"
          //         onClick={() => stopSwarm(params.row)}
          //         disabled={isInEditMode}
          //       />
          //     ] : [
          //       <GridActionsCellItem
          //         icon={<PlayCircleFilledIcon color={isInEditMode ? 'disabled' : 'success'} sx={{ fontSize: '1.75em' }} />}
          //         label="Start Swarm"
          //         onClick={() => startSwarm(params.row)}
          //         disabled={isInEditMode}
          //       />
          //     ]}
          //   </>
          // )
        }
      }
    ])
  }, [
    rowModesModel,
    startSwarm,
    stopSwarm,
  ]);

  return {
    getColumns,
  };
};

export default useSwarmColumns;
